import pick from 'lodash/pick';

import {
  types as sdkTypes,
  createImageVariantConfig,
  createScaledImageVariantConfig,
} from '../../util/sdkLoader';
import { storableError } from '../../util/errors';
import { addMarketplaceEntities } from '../../ducks/marketplaceData.duck';
import { transactionLineItems, getBundles } from '../../util/api';
import * as log from '../../util/log';
import { denormalisedResponseEntities } from '../../util/data';
import {
  bookingTimeUnits,
  findNextBoundary,
  getStartOf,
  monthIdString,
  stringifyDateToISO8601,
} from '../../util/dates';
import {
  hasPermissionToInitiateTransactions,
  hasPermissionToViewData,
  isUserAuthorized,
} from '../../util/userHelpers';
import {
  LISTING_PAGE_DRAFT_VARIANT,
  LISTING_PAGE_PENDING_APPROVAL_VARIANT,
} from '../../util/urlHelpers';
import {
  getProcess,
  isBookingProcessAlias,
  isPurchaseProcess,
  resolveLatestProcessName,
} from '../../transactions/transaction';
import { LISTING_STATE_CLOSED, LINE_ITEM_ITEM } from '../../util/types';
import { fetchCurrentUser, fetchCurrentUserHasOrdersSuccess } from '../../ducks/user.duck';

const { UUID } = sdkTypes;
const MINUTE_IN_MS = 1000 * 60;

// Day-based time slots queries are cached for 1 minute.
const removeOutdatedDateData = timeSlotsForDate => {
  const now = new Date().getTime();
  const minuteAgo = now - MINUTE_IN_MS;
  return Object.fromEntries(
    Object.entries(timeSlotsForDate).filter(([dateId, data]) => {
      return data.fetchedAt && data.fetchedAt > minuteAgo;
    })
  );
};

// ================ Action types ================ //

export const SET_INITIAL_VALUES = 'app/ListingPage/SET_INITIAL_VALUES';

export const SHOW_LISTING_REQUEST = 'app/ListingPage/SHOW_LISTING_REQUEST';
export const SHOW_LISTING_SUCCESS = 'app/ListingPage/SHOW_LISTING_SUCCESS';
export const SHOW_LISTING_ERROR = 'app/ListingPage/SHOW_LISTING_ERROR';

export const FETCH_REVIEWS_REQUEST = 'app/ListingPage/FETCH_REVIEWS_REQUEST';
export const FETCH_REVIEWS_SUCCESS = 'app/ListingPage/FETCH_REVIEWS_SUCCESS';
export const FETCH_REVIEWS_ERROR = 'app/ListingPage/FETCH_REVIEWS_ERROR';

export const FETCH_MONTHLY_TIME_SLOTS_REQUEST = 'app/ListingPage/FETCH_MONTHLY_TIME_SLOTS_REQUEST';
export const FETCH_MONTHLY_TIME_SLOTS_SUCCESS = 'app/ListingPage/FETCH_MONTHLY_TIME_SLOTS_SUCCESS';
export const FETCH_MONTHLY_TIME_SLOTS_ERROR = 'app/ListingPage/FETCH_MONTHLY_TIME_SLOTS_ERROR';

export const FETCH_TIME_SLOTS_FOR_DATE_REQUEST =
  'app/ListingPage/FETCH_TIME_SLOTS_FOR_DATE_REQUEST';
export const FETCH_TIME_SLOTS_FOR_DATE_SUCCESS =
  'app/ListingPage/FETCH_TIME_SLOTS_FOR_DATE_SUCCESS';
export const FETCH_TIME_SLOTS_FOR_DATE_ERROR = 'app/ListingPage/FETCH_TIME_SLOTS_FOR_DATE_ERROR';

export const FETCH_LINE_ITEMS_REQUEST = 'app/ListingPage/FETCH_LINE_ITEMS_REQUEST';
export const FETCH_LINE_ITEMS_SUCCESS = 'app/ListingPage/FETCH_LINE_ITEMS_SUCCESS';
export const FETCH_LINE_ITEMS_ERROR = 'app/ListingPage/FETCH_LINE_ITEMS_ERROR';

export const SEND_INQUIRY_REQUEST = 'app/ListingPage/SEND_INQUIRY_REQUEST';
export const SEND_INQUIRY_SUCCESS = 'app/ListingPage/SEND_INQUIRY_SUCCESS';
export const SEND_INQUIRY_ERROR = 'app/ListingPage/SEND_INQUIRY_ERROR';

export const FETCH_SIMILAR_LISTINGS_REQUEST = 'app/ListingPage/FETCH_SIMILAR_LISTINGS_REQUEST';
export const FETCH_SIMILAR_LISTINGS_SUCCESS = 'app/ListingPage/FETCH_SIMILAR_LISTINGS_SUCCESS';
export const FETCH_SIMILAR_LISTINGS_ERROR = 'app/ListingPage/FETCH_SIMILAR_LISTINGS_ERROR';

export const FETCH_BRAND_LISTINGS_REQUEST = 'app/ListingPage/FETCH_BRAND_LISTINGS_REQUEST';
export const FETCH_BRAND_LISTINGS_SUCCESS = 'app/ListingPage/FETCH_BRAND_LISTINGS_SUCCESS';
export const FETCH_BRAND_LISTINGS_ERROR = 'app/ListingPage/FETCH_BRAND_LISTINGS_ERROR';

export const FETCH_BUNDLES_REQUEST = 'app/ListingPage/FETCH_BUNDLES_REQUEST';
export const FETCH_BUNDLES_SUCCESS = 'app/ListingPage/FETCH_BUNDLES_SUCCESS';
export const FETCH_BUNDLES_ERROR = 'app/ListingPage/FETCH_BUNDLES_ERROR';

// ================ Reducer ================ //

const initialState = {
  id: null,
  showListingInProgress: false,
  showListingError: null,
  reviews: [],
  fetchReviewsError: null,
  monthlyTimeSlots: {
    // '2022-03': {
    //   timeSlots: [],
    //   fetchTimeSlotsError: null,
    //   fetchTimeSlotsInProgress: null,
    // },
  },
  timeSlotsForDate: {
    // For small time units, we fetch monthly time slots with sparse mode for calendar view
    // and when the user clicks on a day, we make a full time slot query. This is for that purpose.
    // '2025-02-03': {
    //   timeSlots: [],
    //   fetchedAt: 1738569600000,
    //   fetchTimeSlotsError: null,
    //   fetchTimeSlotsInProgress: null,
    // },
  },
  lineItems: null,
  fetchLineItemsInProgress: false,
  fetchLineItemsError: null,
  sendInquiryInProgress: false,
  sendInquiryError: null,
  inquiryModalOpenForListingId: null,
  similarListingIds: [],
  fetchSimilarListingsInProgress: false,
  fetchSimilarListingsError: null,
  brandListingIds: [],
  fetchBrandListingsInProgress: false,
  fetchBrandListingsError: null,
  bundles: [],
  bundleProductIds: [],
  fetchBundlesInProgress: false,
  fetchBundlesError: null,
};

const listingPageReducer = (state = initialState, action = {}) => {
  const { type, payload } = action;
  switch (type) {
    case SET_INITIAL_VALUES:
      return { ...initialState, ...payload };

    case SHOW_LISTING_REQUEST:
      return {
        ...state,
        id: payload.id,
        showListingInProgress: true,
        showListingError: null,
      };
    case SHOW_LISTING_SUCCESS:
      return { ...state, showListingInProgress: false };
    case SHOW_LISTING_ERROR:
      return { ...state, showListingInProgress: false, showListingError: payload };

    case FETCH_REVIEWS_REQUEST:
      return { ...state, fetchReviewsError: null };
    case FETCH_REVIEWS_SUCCESS:
      return { ...state, reviews: payload };
    case FETCH_REVIEWS_ERROR:
      return { ...state, fetchReviewsError: payload };

    case FETCH_MONTHLY_TIME_SLOTS_REQUEST: {
      const monthlyTimeSlots = {
        ...state.monthlyTimeSlots,
        [payload]: {
          ...state.monthlyTimeSlots[payload],
          fetchTimeSlotsError: null,
          fetchTimeSlotsInProgress: true,
        },
      };
      return { ...state, monthlyTimeSlots };
    }
    case FETCH_MONTHLY_TIME_SLOTS_SUCCESS: {
      const monthId = payload.monthId;
      const monthlyTimeSlots = {
        ...state.monthlyTimeSlots,
        [monthId]: {
          ...state.monthlyTimeSlots[monthId],
          fetchTimeSlotsInProgress: false,
          timeSlots: payload.timeSlots,
        },
      };
      return { ...state, monthlyTimeSlots };
    }
    case FETCH_MONTHLY_TIME_SLOTS_ERROR: {
      const monthId = payload.monthId;
      const monthlyTimeSlots = {
        ...state.monthlyTimeSlots,
        [monthId]: {
          ...state.monthlyTimeSlots[monthId],
          fetchTimeSlotsInProgress: false,
          fetchTimeSlotsError: payload.error,
        },
      };
      return { ...state, monthlyTimeSlots };
    }
    case FETCH_TIME_SLOTS_FOR_DATE_REQUEST: {
      const timeSlotsForDate = {
        ...removeOutdatedDateData(state.timeSlotsForDate),
        [payload]: {
          ...state.timeSlotsForDate[payload],
          fetchTimeSlotsError: null,
          fetchedAt: null,
          fetchTimeSlotsInProgress: true,
          timeSlots: [],
        },
      };
      return { ...state, timeSlotsForDate };
    }
    case FETCH_TIME_SLOTS_FOR_DATE_SUCCESS: {
      const dateId = payload.dateId;
      const timeSlotsForDate = {
        ...state.timeSlotsForDate,
        [dateId]: {
          ...state.timeSlotsForDate[dateId],
          fetchTimeSlotsInProgress: false,
          fetchedAt: new Date().getTime(),
          timeSlots: payload.timeSlots,
        },
      };
      return { ...state, timeSlotsForDate };
    }
    case FETCH_TIME_SLOTS_FOR_DATE_ERROR: {
      const dateId = payload.dateId;
      const timeSlotsForDate = {
        ...state.timeSlotsForDate,
        [dateId]: {
          ...state.timeSlotsForDate[dateId],
          fetchTimeSlotsInProgress: false,
          fetchTimeSlotsError: payload.error,
        },
      };
      return { ...state, timeSlotsForDate };
    }

    case FETCH_LINE_ITEMS_REQUEST:
      return { ...state, fetchLineItemsInProgress: true, fetchLineItemsError: null };
    case FETCH_LINE_ITEMS_SUCCESS:
      return { ...state, fetchLineItemsInProgress: false, lineItems: payload };
    case FETCH_LINE_ITEMS_ERROR:
      return { ...state, fetchLineItemsInProgress: false, fetchLineItemsError: payload };

    case SEND_INQUIRY_REQUEST:
      return { ...state, sendInquiryInProgress: true, sendInquiryError: null };
    case SEND_INQUIRY_SUCCESS:
      return { ...state, sendInquiryInProgress: false, inquiryModalOpenForListingId: null };
    case SEND_INQUIRY_ERROR:
      return { ...state, sendInquiryInProgress: false, sendInquiryError: payload };

    case FETCH_SIMILAR_LISTINGS_REQUEST:
      return { ...state, fetchSimilarListingsInProgress: true, fetchSimilarListingsError: null };
    case FETCH_SIMILAR_LISTINGS_SUCCESS:
      return { ...state, fetchSimilarListingsInProgress: false, similarListingIds: payload };
    case FETCH_SIMILAR_LISTINGS_ERROR:
      return {
        ...state,
        fetchSimilarListingsInProgress: false,
        fetchSimilarListingsError: payload,
      };

    case FETCH_BRAND_LISTINGS_REQUEST:
      return { ...state, fetchBrandListingsInProgress: true, fetchBrandListingsError: null };
    case FETCH_BRAND_LISTINGS_SUCCESS:
      return { ...state, fetchBrandListingsInProgress: false, brandListingIds: payload };
    case FETCH_BRAND_LISTINGS_ERROR:
      return { ...state, fetchBrandListingsInProgress: false, fetchBrandListingsError: payload };

    case FETCH_BUNDLES_REQUEST:
      return { ...state, fetchBundlesInProgress: true, fetchBundlesError: null };
    case FETCH_BUNDLES_SUCCESS:
      return {
        ...state,
        fetchBundlesInProgress: false,
        bundles: Array.isArray(payload?.bundles) ? payload.bundles : [],
        bundleProductIds: Array.isArray(payload?.productIds) ? payload.productIds : [],
      };
    case FETCH_BUNDLES_ERROR:
      return {
        ...state,
        fetchBundlesInProgress: false,
        fetchBundlesError: payload,
        bundles: [],
        bundleProductIds: [],
      };

    default:
      return state;
  }
};

export default listingPageReducer;

// ================ Action creators ================ //

export const setInitialValues = initialValues => ({
  type: SET_INITIAL_VALUES,
  payload: pick(initialValues, Object.keys(initialState)),
});

export const showListingRequest = id => ({
  type: SHOW_LISTING_REQUEST,
  payload: { id },
});

export const showListingSuccess = () => ({
  type: SHOW_LISTING_SUCCESS,
});

export const showListingError = e => ({
  type: SHOW_LISTING_ERROR,
  error: true,
  payload: e,
});

export const fetchReviewsRequest = () => ({ type: FETCH_REVIEWS_REQUEST });
export const fetchReviewsSuccess = reviews => ({ type: FETCH_REVIEWS_SUCCESS, payload: reviews });
export const fetchReviewsError = error => ({
  type: FETCH_REVIEWS_ERROR,
  error: true,
  payload: error,
});

export const fetchMonthlyTimeSlotsRequest = monthId => ({
  type: FETCH_MONTHLY_TIME_SLOTS_REQUEST,
  payload: monthId,
});
export const fetchMonthlyTimeSlotsSuccess = (monthId, timeSlots) => ({
  type: FETCH_MONTHLY_TIME_SLOTS_SUCCESS,
  payload: { timeSlots, monthId },
});
export const fetchMonthlyTimeSlotsError = (monthId, error) => ({
  type: FETCH_MONTHLY_TIME_SLOTS_ERROR,
  error: true,
  payload: { monthId, error },
});

export const fetchTimeSlotsForDateRequest = dateId => ({
  type: FETCH_TIME_SLOTS_FOR_DATE_REQUEST,
  payload: dateId,
});
export const fetchTimeSlotsForDateSuccess = (dateId, timeSlots) => ({
  type: FETCH_TIME_SLOTS_FOR_DATE_SUCCESS,
  payload: { timeSlots, dateId },
});
export const fetchTimeSlotsForDateError = (dateId, error) => ({
  type: FETCH_TIME_SLOTS_FOR_DATE_ERROR,
  error: true,
  payload: { dateId, error },
});

export const fetchLineItemsRequest = () => ({ type: FETCH_LINE_ITEMS_REQUEST });
export const fetchLineItemsSuccess = lineItems => ({
  type: FETCH_LINE_ITEMS_SUCCESS,
  payload: lineItems,
});
export const fetchLineItemsError = error => ({
  type: FETCH_LINE_ITEMS_ERROR,
  error: true,
  payload: error,
});

export const sendInquiryRequest = () => ({ type: SEND_INQUIRY_REQUEST });
export const sendInquirySuccess = () => ({ type: SEND_INQUIRY_SUCCESS });
export const sendInquiryError = e => ({ type: SEND_INQUIRY_ERROR, error: true, payload: e });

export const fetchSimilarListingsRequest = () => ({ type: FETCH_SIMILAR_LISTINGS_REQUEST });
export const fetchSimilarListingsSuccess = listingIds => ({
  type: FETCH_SIMILAR_LISTINGS_SUCCESS,
  payload: listingIds,
});
export const fetchSimilarListingsError = e => ({
  type: FETCH_SIMILAR_LISTINGS_ERROR,
  error: true,
  payload: e,
});

export const fetchBrandListingsRequest = () => ({ type: FETCH_BRAND_LISTINGS_REQUEST });
export const fetchBrandListingsSuccess = listingIds => ({
  type: FETCH_BRAND_LISTINGS_SUCCESS,
  payload: listingIds,
});
export const fetchBrandListingsError = e => ({
  type: FETCH_BRAND_LISTINGS_ERROR,
  error: true,
  payload: e,
});

export const fetchBundlesRequest = () => ({ type: FETCH_BUNDLES_REQUEST });
export const fetchBundlesSuccess = ({ bundles, productIds }) => ({
  type: FETCH_BUNDLES_SUCCESS,
  payload: { bundles, productIds },
});
export const fetchBundlesError = e => ({
  type: FETCH_BUNDLES_ERROR,
  error: true,
  payload: e,
});

// ================ Helpers ================ //

const shuffleArray = arr => {
  const shuffled = [...arr];
  for (let i = shuffled.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [shuffled[i], shuffled[j]] = [shuffled[j], shuffled[i]];
  }
  return shuffled;
};

const sortListingsBySortingPriority = listings => {
  const withPriority = listings.filter(l => l.attributes?.publicData?.sorting_priority != null);
  const withoutPriority = listings.filter(l => l.attributes?.publicData?.sorting_priority == null);

  withPriority.sort(
    (a, b) =>
      (b.attributes.publicData.sorting_priority || 0) -
      (a.attributes.publicData.sorting_priority || 0)
  );

  return [...withPriority, ...shuffleArray(withoutPriority)];
};

// ================ Thunks ================ //

export const showListing = (listingId, config, isOwn = false) => (dispatch, getState, sdk) => {
  const {
    aspectWidth = 1,
    aspectHeight = 1,
    variantPrefix = 'listing-card',
  } = config.layout.listingImage;
  const aspectRatio = aspectHeight / aspectWidth;

  dispatch(showListingRequest(listingId));
  // Current user entity is fetched in a bit lazy fashion, since it's not tied to returned Promise chain.
  const fetchCurrentUserOptions = {
    updateHasListings: false,
    updateNotifications: false,
  };
  dispatch(fetchCurrentUser(fetchCurrentUserOptions));

  const params = {
    id: listingId,
    include: ['author', 'author.profileImage', 'images', 'currentStock'],
    'fields.image': [
      // Gallery variants: aspect-ratio preserving, used by SectionGallery
      'variants.listing-gallery',
      'variants.listing-gallery-2x',

      // Scaled variants for large images (platform-defined, may not exist)
      'variants.scaled-small',
      'variants.scaled-medium',
      'variants.scaled-large',
      'variants.scaled-xlarge',

      // Cropped variants for listing thumbnail images
      `variants.${variantPrefix}`,
      `variants.${variantPrefix}-2x`,
      `variants.${variantPrefix}-4x`,
      `variants.${variantPrefix}-6x`,

      // Social media
      'variants.facebook',
      'variants.twitter',

      // Avatars
      'variants.square-small',
      'variants.square-small2x',
    ],
    // Gallery: scale (no crop) — preserves original aspect ratio
    ...createScaledImageVariantConfig('listing-gallery', 900),
    ...createScaledImageVariantConfig('listing-gallery-2x', 1800),
    // Thumbnails: crop to square
    ...createImageVariantConfig(`${variantPrefix}`, 400, aspectRatio),
    ...createImageVariantConfig(`${variantPrefix}-2x`, 800, aspectRatio),
    ...createImageVariantConfig(`${variantPrefix}-4x`, 1600, aspectRatio),
    ...createImageVariantConfig(`${variantPrefix}-6x`, 2400, aspectRatio),
  };

  const show = isOwn ? sdk.ownListings.show(params) : sdk.listings.show(params);

  return show
    .then(data => {
      const listingFields = config?.listing?.listingFields;
      const sanitizeConfig = { listingFields };
      dispatch(addMarketplaceEntities(data, sanitizeConfig));
      dispatch(showListingSuccess());
      return data;
    })
    .catch(e => {
      dispatch(showListingError(storableError(e)));
    });
};

export const fetchReviews = listingId => (dispatch, getState, sdk) => {
  dispatch(fetchReviewsRequest());
  return sdk.reviews
    .query({
      listing_id: listingId,
      state: 'public',
      include: ['author', 'author.profileImage'],
      'fields.image': ['variants.square-small', 'variants.square-small2x'],
    })
    .then(response => {
      const reviews = denormalisedResponseEntities(response);
      dispatch(fetchReviewsSuccess(reviews));
    })
    .catch(e => {
      dispatch(fetchReviewsError(storableError(e)));
    });
};

const timeSlotsRequest = params => (dispatch, getState, sdk) => {
  return sdk.timeslots.query(params).then(response => {
    return denormalisedResponseEntities(response);
  });
};

export const fetchTimeSlots = (listingId, start, end, timeZone, options) => (
  dispatch,
  getState,
  sdk
) => {
  const { extraQueryParams = null, useFetchTimeSlotsForDate = false } = options || {};

  // The maximum pagination page size for timeSlots is 500
  const extraParams = extraQueryParams || {
    perPage: 500,
    page: 1,
  };

  // For small time units, we fetch the data per date.
  // This is to avoid fetching too much data (with 15 minute intervals, there can be 24*4*31 = 2928 time slots)
  if (useFetchTimeSlotsForDate) {
    const dateId = stringifyDateToISO8601(start, timeZone);
    const dateData = getState().ListingPage.timeSlotsForDate[dateId];
    const minuteAgo = new Date().getTime() - MINUTE_IN_MS;
    const hasRecentlyFetchedData = dateData?.fetchedAt > minuteAgo;
    if (hasRecentlyFetchedData) {
      return Promise.resolve(dateData?.timeSlots || []);
    }

    dispatch(fetchTimeSlotsForDateRequest(dateId));
    return dispatch(timeSlotsRequest({ listingId, start, end, ...extraParams }))
      .then(timeSlots => {
        dispatch(fetchTimeSlotsForDateSuccess(dateId, timeSlots));
        return timeSlots;
      })
      .catch(e => {
        dispatch(fetchTimeSlotsForDateError(dateId, storableError(e)));
        return [];
      });
  } else {
    const monthId = monthIdString(start, timeZone);
    dispatch(fetchMonthlyTimeSlotsRequest(monthId));
    return dispatch(timeSlotsRequest({ listingId, start, end, ...extraParams }))
      .then(timeSlots => {
        dispatch(fetchMonthlyTimeSlotsSuccess(monthId, timeSlots));
        return timeSlots;
      })
      .catch(e => {
        dispatch(fetchMonthlyTimeSlotsError(monthId, storableError(e)));
        return [];
      });
  }
};

export const sendInquiry = (listing, message) => (dispatch, getState, sdk) => {
  dispatch(sendInquiryRequest());
  const processAlias = listing?.attributes?.publicData?.transactionProcessAlias;
  if (!processAlias) {
    const error = new Error('No transaction process attached to listing');
    log.error(error, 'listing-process-missing', {
      listingId: listing?.id?.uuid,
    });
    dispatch(sendInquiryError(storableError(error)));
    return Promise.reject(error);
  }

  const listingId = listing?.id;
  const [processName, alias] = processAlias.split('/');
  const transitions = getProcess(processName)?.transitions;

  const bodyParams = {
    transition: transitions.INQUIRE,
    processAlias,
    params: { listingId },
  };
  return sdk.transactions
    .initiate(bodyParams)
    .then(response => {
      const transactionId = response.data.data.id;

      // Send the message to the created transaction
      return sdk.messages.send({ transactionId, content: message }).then(() => {
        dispatch(sendInquirySuccess());
        dispatch(fetchCurrentUserHasOrdersSuccess(true));
        return transactionId;
      });
    })
    .catch(e => {
      dispatch(sendInquiryError(storableError(e)));
      throw e;
    });
};

export const fetchSimilarListings = (currentListingId, categoryLevel1, config) => (
  dispatch,
  getState,
  sdk
) => {
  if (!categoryLevel1) {
    dispatch(fetchSimilarListingsSuccess([]));
    return Promise.resolve();
  }

  if (!sdk?.listings?.query) {
    dispatch(fetchSimilarListingsSuccess([]));
    return Promise.resolve();
  }

  dispatch(fetchSimilarListingsRequest());

  const {
    aspectWidth = 1,
    aspectHeight = 1,
    variantPrefix = 'listing-card',
  } = config.layout.listingImage;
  const aspectRatio = aspectHeight / aspectWidth;

  const params = {
    pub_categoryLevel1: categoryLevel1,
    perPage: 13,
    include: ['author', 'images'],
    'fields.image': [
      'variants.listing-gallery',
      'variants.listing-gallery-2x',
      `variants.${variantPrefix}`,
      `variants.${variantPrefix}-2x`,
    ],
    ...createScaledImageVariantConfig('listing-gallery', 900),
    ...createScaledImageVariantConfig('listing-gallery-2x', 1800),
    ...createImageVariantConfig(`${variantPrefix}`, 400, aspectRatio),
    ...createImageVariantConfig(`${variantPrefix}-2x`, 800, aspectRatio),
  };

  return sdk.listings
    .query(params)
    .then(response => {
      const listingFields = config?.listing?.listingFields;
      const sanitizeConfig = { listingFields };
      dispatch(addMarketplaceEntities(response, sanitizeConfig));

      const currentId = currentListingId?.uuid || currentListingId;
      const listings = response.data.data.filter(l => l.id.uuid !== currentId);
      const ids = sortListingsBySortingPriority(listings).map(l => l.id);

      dispatch(fetchSimilarListingsSuccess(ids));
    })
    .catch(e => {
      dispatch(fetchSimilarListingsError(storableError(e)));
    });
};

export const fetchBrandListings = (currentListingId, brand, config) => (
  dispatch,
  getState,
  sdk
) => {
  if (!brand) {
    dispatch(fetchBrandListingsSuccess([]));
    return Promise.resolve();
  }

  if (!sdk?.listings?.query) {
    dispatch(fetchBrandListingsSuccess([]));
    return Promise.resolve();
  }

  dispatch(fetchBrandListingsRequest());

  const {
    aspectWidth = 1,
    aspectHeight = 1,
    variantPrefix = 'listing-card',
  } = config.layout.listingImage;
  const aspectRatio = aspectHeight / aspectWidth;

  const brandValue = Array.isArray(brand) ? brand[0] : brand;

  const params = {
    pub_brand: brandValue,
    perPage: 13,
    include: ['author', 'images'],
    'fields.image': [
      'variants.listing-gallery',
      'variants.listing-gallery-2x',
      `variants.${variantPrefix}`,
      `variants.${variantPrefix}-2x`,
    ],
    ...createScaledImageVariantConfig('listing-gallery', 900),
    ...createScaledImageVariantConfig('listing-gallery-2x', 1800),
    ...createImageVariantConfig(`${variantPrefix}`, 400, aspectRatio),
    ...createImageVariantConfig(`${variantPrefix}-2x`, 800, aspectRatio),
  };

  return sdk.listings
    .query(params)
    .then(response => {
      const listingFields = config?.listing?.listingFields;
      const sanitizeConfig = { listingFields };
      dispatch(addMarketplaceEntities(response, sanitizeConfig));

      const currentId = currentListingId?.uuid || currentListingId;
      const listings = response.data.data.filter(l => l.id.uuid !== currentId);
      const ids = sortListingsBySortingPriority(listings).map(l => l.id);

      dispatch(fetchBrandListingsSuccess(ids));
    })
    .catch(e => {
      dispatch(fetchBrandListingsError(storableError(e)));
    });
};

// Helper function for loadData call.
// Note: listing could be ownListing entity too
const fetchMonthlyTimeSlots = (dispatch, listing) => {
  const hasWindow = typeof window !== 'undefined';
  const { availabilityPlan, publicData } = listing?.attributes || {};
  const tz = availabilityPlan?.timezone;

  // Fetch time-zones on client side only.
  if (hasWindow && listing.id && !!tz) {
    const { unitType, priceVariants, startTimeInterval } = publicData || {};
    const now = new Date();
    const startOfToday = getStartOf(now, 'day', tz);
    const isFixed = unitType === 'fixed';

    const timeUnit = startTimeInterval
      ? bookingTimeUnits[startTimeInterval]?.timeUnit
      : unitType === 'hour'
      ? 'hour'
      : 'day';
    const nextBoundary = findNextBoundary(now, 1, timeUnit, tz);

    const nextMonth = getStartOf(nextBoundary, 'month', tz, 1, 'months');
    const nextAfterNextMonth = getStartOf(nextMonth, 'month', tz, 1, 'months');

    const variants = priceVariants || [];
    const bookingLengthInMinutes = variants.reduce((min, priceVariant) => {
      return Math.min(min, priceVariant.bookingLengthInMinutes);
    }, Number.MAX_SAFE_INTEGER);

    const nextMonthEnd = isFixed
      ? getStartOf(nextMonth, 'minute', tz, bookingLengthInMinutes, 'minutes')
      : nextMonth;
    const followingMonthEnd = isFixed
      ? getStartOf(nextAfterNextMonth, 'minute', tz, bookingLengthInMinutes, 'minutes')
      : nextAfterNextMonth;

    const minDurationStartingInInterval = isFixed ? bookingLengthInMinutes : 60;

    const options = intervalAlign => {
      return ['fixed', 'hour'].includes(unitType)
        ? {
            extraQueryParams: {
              intervalDuration: 'P1D',
              intervalAlign,
              maxPerInterval: 1,
              minDurationStartingInInterval,
              perPage: 31,
              page: 1,
            },
          }
        : null;
    };

    return Promise.all([
      dispatch(fetchTimeSlots(listing.id, nextBoundary, nextMonthEnd, tz, options(startOfToday))),
      dispatch(fetchTimeSlots(listing.id, nextMonth, followingMonthEnd, tz, options(nextMonth))),
    ]);
  }

  // By default return an empty array
  return Promise.all([]);
};

// Global rate limiting for API calls
let lastApiCallTime = 0;
const MIN_API_CALL_INTERVAL = 500; // Minimum 500ms between API calls

export const fetchTransactionLineItems = ({ orderData, listingId, isOwnListing }) => (
  dispatch,
  getState
) => {
  // Check if we're already in progress to prevent duplicate calls
  const state = getState();
  if (state.ListingPage?.fetchLineItemsInProgress) {
    console.warn('fetchTransactionLineItems: Already in progress, skipping duplicate call');
    return Promise.resolve();
  }

  // Rate limiting check
  const now = Date.now();
  if (now - lastApiCallTime < MIN_API_CALL_INTERVAL) {
    console.warn('fetchTransactionLineItems: Rate limited, skipping call');
    return Promise.resolve();
  }

  lastApiCallTime = now;
  dispatch(fetchLineItemsRequest());
  return transactionLineItems({ orderData, listingId, isOwnListing })
    .then(response => {
      const lineItems = response.data;
      dispatch(fetchLineItemsSuccess(lineItems));
    })
    .catch(e => {
      // Special handling for rate limiting errors
      if (e.status === 429) {
        console.warn('fetchTransactionLineItems: Rate limited (429), will not retry immediately');
        // Set a longer delay for rate limited requests
        setTimeout(() => {
          dispatch(fetchLineItemsError(storableError(e)));
        }, 1000); // 1 second delay
      } else {
        dispatch(fetchLineItemsError(storableError(e)));
      }

      log.error(e, 'fetching-line-items-failed', {
        listingId: listingId?.uuid || listingId,
        orderData,
        statusText: e.statusText,
      });
    });
};

// Safely convert a list of string IDs to UUID SDK instances. Invalid values
// are silently dropped so one bad DB row can't crash the render.
const toUuidRefs = ids =>
  (Array.isArray(ids) ? ids : [])
    .map(id => {
      try {
        return typeof id === 'string' && id ? new UUID(id) : null;
      } catch (e) {
        return null;
      }
    })
    .filter(Boolean);

export const fetchBundles = (listing, config) => (dispatch, getState, sdk) => {
  const publicData = listing?.attributes?.publicData;
  const rawIds = publicData?.bundles;

  // Accept 3 shapes defensively:
  // - native array:           ["id1", "id2"]
  // - comma-separated string: "id1,id2"
  // - JSON-encoded array:     '["id1", "id2"]'
  let parsedIds = null;
  let rawShape = 'unknown';
  if (Array.isArray(rawIds)) {
    parsedIds = rawIds;
    rawShape = 'array';
  } else if (typeof rawIds === 'string' && rawIds.trim()) {
    const trimmed = rawIds.trim();
    if (trimmed.startsWith('[')) {
      try {
        const maybe = JSON.parse(trimmed);
        if (Array.isArray(maybe)) {
          parsedIds = maybe;
          rawShape = 'json-string';
        }
      } catch (_) {
        rawShape = 'invalid-json';
      }
    }
    if (parsedIds == null) {
      parsedIds = trimmed.split(',');
      rawShape = rawShape === 'unknown' ? 'csv-string' : rawShape;
    }
  }

  const bundleIds = Array.isArray(parsedIds)
    ? parsedIds
        .map(id => (typeof id === 'string' ? id.trim() : null))
        .filter(id => id && id.length > 0)
    : [];
  const listingUuid = listing?.id?.uuid || null;

  if (bundleIds.length === 0 && !listingUuid) {
    dispatch(fetchBundlesSuccess({ bundles: [], productIds: [] }));
    return Promise.resolve({ bundles: [], productIds: [] });
  }

  dispatch(fetchBundlesRequest());

  // Query by publicData.bundles (ids) AND by listingId in parallel, then merge.
  // This way a bundle attached via the DB column `listing_ids` also shows up
  // even if the listing's publicData.bundles array hasn't been updated.
  const byIdsPromise =
    bundleIds.length > 0
      ? getBundles({ ids: bundleIds }).catch(() => ({ data: [] }))
      : Promise.resolve({ data: [] });

  const byListingPromise = listingUuid
    ? getBundles({ listingId: listingUuid }).catch(() => ({ data: [] }))
    : Promise.resolve({ data: [] });

  return Promise.all([byIdsPromise, byListingPromise])
    .then(([byIdsResp, byListingResp]) => {
      const byIds = Array.isArray(byIdsResp?.data) ? byIdsResp.data : [];
      const byListing = Array.isArray(byListingResp?.data) ? byListingResp.data : [];

      // Merge + dedupe by id (preserve byIds order first, then any extra from byListing).
      const seen = new Set();
      const bundles = [];
      for (const b of [...byIds, ...byListing]) {
        if (b && b.id && !seen.has(b.id)) {
          seen.add(b.id);
          bundles.push(b);
        }
      }

      const productIdsSet = new Set();
      for (const b of bundles) {
        if (Array.isArray(b.products)) {
          for (const p of b.products) {
            if (typeof p?.id === 'string' && p.id) productIdsSet.add(p.id);
          }
        }
      }
      const productIds = Array.from(productIdsSet);

      if (productIds.length === 0) {
        dispatch(fetchBundlesSuccess({ bundles, productIds: [] }));
        return { bundles, productIds: [] };
      }

      const {
        aspectWidth = 1,
        aspectHeight = 1,
        variantPrefix = 'listing-card',
      } = config.layout.listingImage;
      const aspectRatio = aspectHeight / aspectWidth;

      const commonShowParams = {
        include: ['author', 'images'],
        'fields.image': [
          `variants.${variantPrefix}`,
          `variants.${variantPrefix}-2x`,
          'variants.listing-gallery',
          'variants.listing-gallery-2x',
        ],
        ...createImageVariantConfig(`${variantPrefix}`, 400, aspectRatio),
        ...createImageVariantConfig(`${variantPrefix}-2x`, 800, aspectRatio),
        ...createScaledImageVariantConfig('listing-gallery', 900),
        ...createScaledImageVariantConfig('listing-gallery-2x', 1800),
      };

      // Use `sdk.listings.show` per product instead of `sdk.listings.query({ ids })`.
      // `query` implicitly filters on `states=['published']`, so any listing in
      // `closed` or similar state would be silently dropped — whereas the live
      // `/product/<id>` page uses `show` and works for those states too.
      const showPromises = productIds.map(id =>
        sdk.listings
          .show({ id: new UUID(id), ...commonShowParams })
          .then(resp => ({ ok: true, id, resp }))
          .catch(err => ({ ok: false, id, err }))
      );

      return Promise.all(showPromises).then(results => {
        const listingFields = config?.listing?.listingFields;
        const sanitizeConfig = { listingFields };

        const okResults = results.filter(r => r.ok);

        okResults.forEach(r => {
          dispatch(addMarketplaceEntities(r.resp, sanitizeConfig));
        });

        // Store product IDs as UUID instances so `getMarketplaceEntities`
        // can resolve them (it expects refs with `id.uuid`, not plain strings).
        const productUuidRefs = toUuidRefs(productIds);
        dispatch(fetchBundlesSuccess({ bundles, productIds: productUuidRefs }));
        return { bundles, productIds: productUuidRefs };
      });
    })
    .catch(e => {
      log.error(e, 'fetch-bundles-failed', { listingId: listingUuid });
      dispatch(fetchBundlesError(storableError(e)));
    });
};

/**
 * Prefetch default purchase line-items once the listing is known so ProductOrderForm
 * does not show a second loading phase. Client-only: util/api uses window.fetch.
 */
const maybePrefetchLineItems = (dispatch, listingResponse, isOwnListing) => {
  if (typeof window === 'undefined' || !listingResponse?.data) {
    return;
  }

  const entities = denormalisedResponseEntities(listingResponse);
  const listing = entities[0];
  if (!listing) {
    return;
  }

  const publicData = listing.attributes?.publicData || {};
  const { unitType, transactionProcessAlias = '', shippingEnabled, pickupEnabled } = publicData;
  const processName = resolveLatestProcessName(transactionProcessAlias.split('/')[0]);
  const isPurchase = isPurchaseProcess(processName);
  const lineItemUnitType = `line-item/${unitType}`;
  const isClosed = listing.attributes?.state === LISTING_STATE_CLOSED;
  const currentStock = listing.currentStock?.attributes?.quantity;

  const shouldPrefetch =
    isPurchase &&
    lineItemUnitType === LINE_ITEM_ITEM &&
    !isClosed &&
    typeof currentStock === 'number' &&
    currentStock > 0;

  if (!shouldPrefetch) {
    return;
  }

  const deliveryMethod = shippingEnabled ? 'shipping' : pickupEnabled ? 'pickup' : 'shipping';

  dispatch(
    fetchTransactionLineItems({
      orderData: { stockReservationQuantity: 1, deliveryMethod },
      listingId: listing.id,
      isOwnListing: !!isOwnListing,
    })
  );
};

export const loadData = (params, search, config) => (dispatch, getState, sdk) => {
  const listingId = new UUID(params.id);
  const state = getState();
  const currentUser = state.user?.currentUser;
  const inquiryModalOpenForListingId =
    isUserAuthorized(currentUser) && hasPermissionToInitiateTransactions(currentUser)
      ? state.ListingPage.inquiryModalOpenForListingId
      : null;

  // Keep line-items when remounting the same listing (client loadData after SSR).
  const prevListingId = state.ListingPage?.id;
  const sameListing = prevListingId?.uuid === listingId.uuid;
  const preservedLineItems = sameListing ? state.ListingPage.lineItems : null;

  dispatch(setInitialValues({ lineItems: preservedLineItems, inquiryModalOpenForListingId }));

  const ownListingVariants = [LISTING_PAGE_DRAFT_VARIANT, LISTING_PAGE_PENDING_APPROVAL_VARIANT];
  if (ownListingVariants.includes(params.variant)) {
    return dispatch(showListing(listingId, config, true)).then(listingResponse => {
      if (listingResponse && !preservedLineItems) {
        maybePrefetchLineItems(dispatch, listingResponse, true);
      }
      return listingResponse;
    });
  }

  // In private marketplace mode, this page won't fetch data if the user is unauthorized
  const isAuthorized = currentUser && isUserAuthorized(currentUser);
  const isPrivateMarketplace = config.accessControl.marketplace.private === true;
  const canFetchData = !isPrivateMarketplace || (isPrivateMarketplace && isAuthorized);
  if (!canFetchData) {
    return Promise.resolve();
  }

  const hasNoViewingRights = currentUser && !hasPermissionToViewData(currentUser);
  const promises = hasNoViewingRights
    ? // If user has no viewing rights, only allow fetching their own listing without reviews
      [dispatch(showListing(listingId, config, true))]
    : // For users with viewing rights, fetch the listing and the associated reviews
      [dispatch(showListing(listingId, config)), dispatch(fetchReviews(listingId))];

  return Promise.all(promises).then(response => {
    const listingResponse = response[0];
    const listing = listingResponse?.data?.data;
    const transactionProcessAlias = listing?.attributes?.publicData?.transactionProcessAlias || '';
    if (isBookingProcessAlias(transactionProcessAlias) && !hasNoViewingRights) {
      fetchMonthlyTimeSlots(dispatch, listing);
    }

    if (listing && !hasNoViewingRights) {
      dispatch(fetchBundles(listing, config));
    }

    if (listingResponse && !preservedLineItems) {
      maybePrefetchLineItems(dispatch, listingResponse, !!hasNoViewingRights);
    }

    return response;
  });
};
